import { type MbscResource } from '@mobiscroll/react';

import {
  CalendarGruppenraumResource,
  calendarResourceTypes,
  CalendarSeminarraumResource,
} from '../../components/general';
import { CalendarStandortResource } from '../../components/general/ResourceCalendar';
import { LocationResource } from '../../components/general/ResourceCalendar/helperClasses/CalendarResource/LocationResource';
import { calendarColors } from '../../theme/colorPalette';

type SeminarraumResourceGroup = Omit<MbscResource, 'id' | 'name' | 'children'> & {
  id: typeof calendarResourceTypes.SEMINARRAUM;
  name: typeof calenderResourceGroupLabels.SEMINARRAUM;
  children: LocationResource<CalendarSeminarraumResource>[];
};

type GruppenraumResourceGroup = Omit<MbscResource, 'id' | 'name' | 'children'> & {
  id: typeof calendarResourceTypes.GRUPPENRAUM;
  name: typeof calenderResourceGroupLabels.GRUPPENRAUM;
  children: LocationResource<CalendarGruppenraumResource>[];
};

type StandortResourceGroup = Omit<MbscResource, 'id' | 'name' | 'children'> & {
  id: typeof calendarResourceTypes.STANDORT;
  name: typeof calenderResourceGroupLabels.STANDORT;
  children: CalendarStandortResource[];
};

type ResourceGroup = SeminarraumResourceGroup | GruppenraumResourceGroup | StandortResourceGroup;

export type RaumCollapseInfo = {
  seminarraum: boolean;
  gruppenraum: boolean;
  standort: boolean;
};

const calenderResourceGroupLabels = {
  SEMINARRAUM: 'Seminarräume',
  GRUPPENRAUM: 'Gruppenräume',
  STANDORT: 'Standorte',
} as const;

const getGroupDefaultValues = (
  collapseInfo: RaumCollapseInfo,
): {
  seminarraeumeGroup: SeminarraumResourceGroup;
  gruppenraeumeGroup: GruppenraumResourceGroup;
  standorteGroup: StandortResourceGroup;
} => {
  const seminarraeumeGroup: SeminarraumResourceGroup = {
    id: calendarResourceTypes.SEMINARRAUM,
    name: calenderResourceGroupLabels.SEMINARRAUM,
    children: [],
    collapsed: collapseInfo.seminarraum,
    background: calendarColors.accordion,
  };

  const gruppenraeumeGroup: GruppenraumResourceGroup = {
    id: calendarResourceTypes.GRUPPENRAUM,
    name: calenderResourceGroupLabels.GRUPPENRAUM,
    children: [],
    collapsed: collapseInfo.gruppenraum,
    background: calendarColors.accordion,
  };

  const standorteGroup: StandortResourceGroup = {
    id: calendarResourceTypes.STANDORT,
    name: calenderResourceGroupLabels.STANDORT,
    children: [],
    collapsed: collapseInfo.standort,
    background: calendarColors.accordion,
  };

  return {
    seminarraeumeGroup,
    gruppenraeumeGroup,
    standorteGroup,
  };
};

export const groupRaumResources = (
  seminarraumResources: CalendarSeminarraumResource[],
  gruppenraumResources: CalendarGruppenraumResource[],
  standortResources: CalendarStandortResource[],
  collapseInfo: RaumCollapseInfo,
): MbscResource[] & ResourceGroup[] => {
  const { seminarraeumeGroup, gruppenraeumeGroup, standorteGroup } = getGroupDefaultValues({ ...collapseInfo });

  seminarraeumeGroup.collapsed = collapseInfo.seminarraum;
  gruppenraeumeGroup.collapsed = collapseInfo.gruppenraum;

  const seminarraeumeByLocationMap = new Map<string, CalendarSeminarraumResource[]>();

  for (const resource of seminarraumResources) {
    const { location } = resource;
    if (!seminarraeumeByLocationMap.has(location.kuerzel)) {
      seminarraeumeByLocationMap.set(location.kuerzel, []);
    }
    const resourcesForLocation = seminarraeumeByLocationMap.get(location.kuerzel);
    if (resourcesForLocation) {
      resourcesForLocation.push(resource);
    }
  }

  const gruppenraeumeByLocationMap = new Map<string, CalendarGruppenraumResource[]>();
  for (const resource of gruppenraumResources) {
    const { location } = resource;
    if (!gruppenraeumeByLocationMap.has(location.kuerzel)) {
      gruppenraeumeByLocationMap.set(location.kuerzel, []);
    }

    const resourcesForLocation = gruppenraeumeByLocationMap.get(location.kuerzel);
    if (resourcesForLocation) {
      resourcesForLocation.push(resource);
    }
  }

  for (const resource of standortResources) {
    standorteGroup.children.push(resource);
  }

  // locations mit seminarräumen hinzufügen
  for (const [, seminarraumResources] of seminarraeumeByLocationMap) {
    if (seminarraumResources.length > 0) {
      const { location } = seminarraumResources[0];
      const locationResource = new LocationResource<CalendarSeminarraumResource>(
        location,
        calendarResourceTypes.SEMINARRAUM,
        seminarraumResources,
      );
      seminarraeumeGroup.children.push(locationResource);
    }
  }

  // locations mit gruppenräumen hinzufügen
  for (const [, gruppenraeume] of gruppenraeumeByLocationMap) {
    if (gruppenraeume.length > 0) {
      const { location } = gruppenraeume[0];
      const locationResource = new LocationResource<CalendarGruppenraumResource>(
        location,
        calendarResourceTypes.GRUPPENRAUM,
        gruppenraeume,
      );
      gruppenraeumeGroup.children.push(locationResource);
    }
  }

  return [seminarraeumeGroup, gruppenraeumeGroup, standorteGroup];
};
